import React, { useMemo } from "react";

interface BodyProps {
  content: string;
  className?: string;
}

// Quotes, angle brackets and "](" end an address, as they do on the server
// (app/services/learner_html.rb).
const urlPattern = /https?:\/\/(?:(?!\]\()[^\s<>"'`])+/g;
const LINK_REL = "nofollow ugc noopener noreferrer";

// A full stop, a closing bracket or Markdown's "**" after an address usually
// belongs to the sentence, not the address (not "_": YouTube ids end in one).
// One pass from the end, counting brackets once, so a long run of them
// cannot hang the page.
const TRAILING_PUNCTUATION = ".,;:!?*~";
const countOf = (text: string, char: string): number =>
  text.split(char).length - 1;

const trimTrailing = (url: string): string => {
  let unmatchedRound = countOf(url, ")") - countOf(url, "(");
  let unmatchedSquare = countOf(url, "]") - countOf(url, "[");
  let end = url.length;
  while (end > 0) {
    const char = url[end - 1];
    if (TRAILING_PUNCTUATION.includes(char)) {
      end -= 1;
    } else if (char === ")" && unmatchedRound > 0) {
      unmatchedRound -= 1;
      end -= 1;
    } else if (char === "]" && unmatchedSquare > 0) {
      unmatchedSquare -= 1;
      end -= 1;
    } else {
      break;
    }
  }
  return url.slice(0, end);
};

// The server sends the post as cleaned HTML with its web addresses as plain
// text; this turns them into links. It works on the parsed text, never on
// the HTML string, so nothing in an address can become part of a tag. A
// <template> is used because nothing parsed into one loads or runs.
const linkify = (html: string): string => {
  if (typeof document === "undefined") return html;

  const template = document.createElement("template");
  template.innerHTML = html;
  const walker = document.createTreeWalker(
    template.content,
    NodeFilter.SHOW_TEXT,
  );
  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

  textNodes.forEach((node) => {
    if (node.parentElement?.closest("a, code, pre")) return;

    const text = node.data;
    const pieces = document.createDocumentFragment();
    let last = 0;
    text.replace(urlPattern, (found: string, offset: number) => {
      const url = trimTrailing(found);
      pieces.append(text.slice(last, offset));
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", LINK_REL);
      link.textContent = url;
      pieces.append(link);
      last = offset + url.length;
      return found;
    });
    if (last === 0) return;

    pieces.append(text.slice(last));
    node.replaceWith(pieces);
  });

  return template.innerHTML;
};

const Body = ({ content, className = "post-body" }: BodyProps) => {
  const linkedContent = useMemo(() => linkify(content || ""), [content]);

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: linkedContent }}
    />
  );
};

export default Body;
